import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/Auth";
import { BrandMark, WaFloat, LegalLinks } from "../components/common";
import { wa, eur0 } from "../lib/fmt";

function Challenge({ value, onChange, reloadKey }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/auth/challenge").then(({ data }) => { setC(data); onChange({ id: data.id, answer: "" }); }).catch(() => {}); // eslint-disable-next-line
  }, [reloadKey]);
  return (
    <div className="field">
      <label htmlFor="challenge">🤖 Comprobación antibots: {c?.question || "cargando…"}</label>
      <input id="challenge" inputMode="numeric" required value={value.answer} onChange={(e) => onChange({ ...value, answer: e.target.value })} placeholder="Tu respuesta" data-testid="challenge-input" />
    </div>
  );
}

function storedRef() {
  try { const r = JSON.parse(localStorage.getItem("cc_ref") || "null"); return r?.code || ""; } catch { return ""; }
}

export default function AuthPage({ initial = "register" }) {
  const nav = useNavigate();
  const { user, setUser, refreshUser, config } = useAuth();
  const [mode, setMode] = useState(user && !user.email_verified ? "verify" : initial);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [ch, setCh] = useState({ id: "", answer: "" });
  const [chKey, setChKey] = useState(0);
  const [f, setF] = useState({ name: "", email: "", password: "", password2: "", terms: false, privacy: false, demo: false, ref: storedRef(), website: "", code: "", totp: "" });
  const [needTotp, setNeedTotp] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  useEffect(() => { if (user && !user.email_verified) setMode("verify"); }, [user]);
  const switchTo = (m) => { setMode(m); setErr(""); setNote(""); };

  const wrap = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr(errMsg(e)); setChKey((k) => k + 1); } finally { setBusy(false); }
  };

  const register = (e) => { e.preventDefault(); wrap(async () => {
    if (f.password !== f.password2) throw { response: { data: { detail: "Las contraseñas no coinciden." } } };
    const { data } = await api.post("/auth/register", { name: f.name, email: f.email, password: f.password, accept_terms: f.terms, accept_privacy: f.privacy, ref_code: f.ref || null, challenge_id: ch.id, challenge_answer: ch.answer, website: f.website });
    setUser(data.user);
    setMode("verify");
    setNote(data.email_sent ? `Te hemos enviado un código de 6 dígitos y un enlace a ${data.user.email}. Revisa también la carpeta de spam.` : "No hemos podido enviar el correo ahora mismo. Pulsa «Reenviar código».");
  }); };

  const login = (e) => { e.preventDefault(); wrap(async () => {
    const { data } = await api.post("/auth/login", { email: f.email, password: f.password, totp: f.totp || null });
    if (data.totp_required) { setNeedTotp(true); setNote("Introduce el código de tu app de autenticación (2FA)."); return; }
    setUser(data.user);
    if (!data.user.email_verified) { setMode("verify"); setNote("Tu correo aún no está verificado. Introduce el código que te enviamos o pide uno nuevo."); return; }
    nav("/panel/inicio");
  }); };

  const verify = (e) => { e.preventDefault(); wrap(async () => {
    await api.post("/auth/verify", { code: f.code.trim() });
    await refreshUser();
    localStorage.removeItem("cc_ref");
    nav("/panel/inicio");
  }); };

  const resend = () => wrap(async () => { await api.post("/auth/resend-verification"); setNote("Código reenviado. Revisa tu bandeja de entrada."); });

  const forgot = (e) => { e.preventDefault(); wrap(async () => {
    const { data } = await api.post("/auth/forgot", { email: f.email, challenge_id: ch.id, challenge_answer: ch.answer });
    setNote(data.message);
    setChKey((k) => k + 1);
  }); };

  const heading = { register: ["Crea tu cuenta gratis", "Regístrate para entrar a tu panel personal."], login: ["Bienvenido de nuevo", "Inicia sesión para continuar con tus misiones."], verify: ["Verifica tu correo", "Necesitamos confirmar tu email antes de activar recompensas."], forgot: ["Recupera tu contraseña", "Te enviaremos un enlace de un solo uso que caduca en 60 minutos."] }[mode];
  const r = config.rewards || {};

  return (
    <>
      <div className="auth-cover" data-testid="auth-page">
        <section className="auth-promo" aria-label="Presentación">
          <BrandMark big testid="auth-brand" />
          <h1 className="auth-splash">Tus misiones.<br /><span>Tu cartera.</span><br />Tus objetivos.</h1>
          <p className="auth-desc">Accede a tu panel personal, participa en desafíos, descubre tus ventajas VIP y sigue una barra de progreso animada hacia los 1.000 €.</p>
          <div className="auth-features">
            <div className="auth-feature"><span>🎁</span>Premio de bienvenida {r.registration ? `· ${eur0(r.registration)}` : ""}</div>
            <div className="auth-feature"><span>🎯</span>Misiones diarias</div>
            <div className="auth-feature"><span>👥</span>Invita a tus amigos {r.referral ? `· ${eur0(r.referral)}` : ""}</div>
            <div className="auth-feature"><span>💰</span>Cartera y niveles VIP</div>
          </div>
          <p className="auth-demo-notice">🔒 Nunca te pediremos pagos, claves privadas ni frases semilla.</p>
        </section>
        <section className="auth-card" aria-label="Registrarse e iniciar sesión">
          <div className="auth-upper"><span className="eyebrow">BIENVENIDO A {config.brand_name}</span><span className="pill">✅ ACTIVO</span></div>
          <h2 data-testid="auth-heading">{heading[0]}</h2>
          <p className="intro">{heading[1]}</p>
          {(mode === "register" || mode === "login") && (
            <div className="auth-switch" role="tablist" aria-label="Elegir acceso">
              <button type="button" className={mode === "register" ? "selected" : ""} role="tab" aria-selected={mode === "register"} onClick={() => switchTo("register")} data-testid="auth-tab-register">🎉 Registrarse</button>
              <button type="button" className={mode === "login" ? "selected" : ""} role="tab" aria-selected={mode === "login"} onClick={() => switchTo("login")} data-testid="auth-tab-login">🔑 Iniciar sesión</button>
            </div>
          )}
          {note && <div className="auth-flow-note" data-testid="auth-note">{note}</div>}

          {mode === "register" && (
            <form className="auth-form visible" onSubmit={register} autoComplete="on" data-testid="register-form">
              <div className="field"><label htmlFor="r-name">Nombre visible</label><input id="r-name" required minLength={2} maxLength={45} value={f.name} onChange={set("name")} autoComplete="nickname" placeholder="Tu nombre" data-testid="register-name-input" /></div>
              <div className="field"><label htmlFor="r-email">Correo electrónico</label><input id="r-email" type="email" required value={f.email} onChange={set("email")} autoComplete="email" placeholder="nombre@ejemplo.com" data-testid="register-email-input" /></div>
              <div className="field"><label htmlFor="r-pass">Contraseña</label><input id="r-pass" type="password" required minLength={8} value={f.password} onChange={set("password")} autoComplete="new-password" placeholder="Mínimo 8 caracteres, letras y números" data-testid="register-password-input" /></div>
              <div className="field"><label htmlFor="r-pass2">Repite tu contraseña</label><input id="r-pass2" type="password" required minLength={8} value={f.password2} onChange={set("password2")} autoComplete="new-password" placeholder="Confirma tu contraseña" data-testid="register-password2-input" /></div>
              <div className="field"><label htmlFor="r-ref">Código de invitación (opcional)</label><input id="r-ref" maxLength={16} value={f.ref} onChange={set("ref")} placeholder="Ej.: A1B2C3D4" data-testid="register-ref-input" /></div>
              <Challenge value={ch} onChange={setCh} reloadKey={chKey} />
              <div className="honeypot" aria-hidden="true"><label>No rellenar<input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} /></label></div>
              <label className="auth-check"><input type="checkbox" required checked={f.terms} onChange={set("terms")} data-testid="register-terms-checkbox" /><span>Acepto los <a href="/legal/terminos" target="_blank" className="lime">Términos de uso</a> y las <a href="/legal/premios" target="_blank" className="lime">Condiciones de premios y retiradas</a>.</span></label>
              <label className="auth-check"><input type="checkbox" required checked={f.privacy} onChange={set("privacy")} data-testid="register-privacy-checkbox" /><span>He leído la <a href="/legal/privacidad" target="_blank" className="lime">Política de privacidad</a> y consiento el tratamiento de señales técnicas antifraude del dispositivo.</span></label>
              <div className="auth-error" role="alert" data-testid="register-error">{err}</div>
              <button type="submit" className="primary" disabled={busy} data-testid="register-submit-btn">{busy ? "Creando cuenta…" : "🎁 Crear cuenta"}</button>
            </form>
          )}

          {mode === "login" && (
            <form className="auth-form visible" onSubmit={login} autoComplete="on" data-testid="login-form">
              <div className="field"><label htmlFor="l-email">Correo electrónico</label><input id="l-email" type="email" required value={f.email} onChange={set("email")} autoComplete="email" placeholder="nombre@ejemplo.com" data-testid="login-email-input" /></div>
              <div className="field"><label htmlFor="l-pass">Contraseña</label><input id="l-pass" type="password" required value={f.password} onChange={set("password")} autoComplete="current-password" placeholder="Introduce tu contraseña" data-testid="login-password-input" /></div>
              {needTotp && <div className="field"><label htmlFor="l-totp">Código 2FA</label><input id="l-totp" inputMode="numeric" maxLength={6} value={f.totp} onChange={set("totp")} data-testid="login-totp-input" /></div>}
              <div className="auth-error" role="alert" data-testid="login-error">{err}</div>
              <button type="submit" className="primary" disabled={busy} data-testid="login-submit-btn">{busy ? "Entrando…" : "🔓 Entrar a mi cuenta"}</button>
            </form>
          )}

          {mode === "verify" && (
            <form className="auth-form visible" onSubmit={verify} data-testid="verify-form">
              <h3>📧 Introduce el código</h3>
              <p className="muted tiny">Enviado a <b>{user?.email}</b>. Sin verificación no se activan premios ni invitaciones válidas.</p>
              <div className="field"><label htmlFor="v-code">Código de 6 dígitos</label><input id="v-code" required maxLength={6} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" value={f.code} onChange={set("code")} data-testid="verify-code-input" /></div>
              <div className="auth-error" role="alert" data-testid="verify-error">{err}</div>
              <button className="primary" type="submit" disabled={busy} data-testid="verify-submit-btn">✅ Verificar y acceder</button>
              <div className="auth-actions"><button type="button" onClick={resend} data-testid="verify-resend-btn">📨 Reenviar código</button><button type="button" onClick={async () => { await api.post("/auth/logout"); setUser(false); switchTo("login"); }} data-testid="verify-logout-btn">↩ Usar otra cuenta</button></div>
            </form>
          )}

          {mode === "forgot" && (
            <form className="auth-form visible" onSubmit={forgot} data-testid="forgot-form">
              <div className="field"><label htmlFor="f-email">Correo registrado</label><input id="f-email" type="email" required value={f.email} onChange={set("email")} placeholder="nombre@ejemplo.com" data-testid="forgot-email-input" /></div>
              <Challenge value={ch} onChange={setCh} reloadKey={chKey} />
              <div className="auth-error" role="alert" data-testid="forgot-error">{err}</div>
              <button type="submit" className="primary" disabled={busy} data-testid="forgot-submit-btn">📧 Enviar enlace de recuperación</button>
              <div className="auth-actions"><button type="button" onClick={() => switchTo("login")} data-testid="forgot-back-btn">← Volver a iniciar sesión</button></div>
            </form>
          )}

          {(mode === "register" || mode === "login") && (
            <div className="auth-actions">
              <button type="button" onClick={() => switchTo("forgot")} data-testid="forgot-link-btn">🔑 He olvidado mi contraseña</button>
              <button type="button" onClick={() => nav("/admin")} data-testid="admin-link-btn">🛡️ Acceso administrador</button>
            </div>
          )}
          <div className="auth-bottom">¿Tienes dudas? <a href={wa()} target="_blank" rel="noopener noreferrer" data-testid="auth-whatsapp-link">💬 Soporte por WhatsApp</a></div>
          <LegalLinks />
        </section>
      </div>
      <WaFloat />
    </>
  );
}
